import type { Middleware, PayloadAction } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { reset as resetSitesOverview } from '~/app/sites/slice'
import { reset as resetVehiclesOverview } from '~/app/vehicles/slice'
import { enhancedApi as rootApi } from '~/data/api/client'
import { enhancedApi as assistantApi } from '~/data/api/client/assistant'
import { type addTagTypes, enhancedApi as billingApi } from '~/data/api/client/billing'
import { enhancedApi as dersApi } from '~/data/api/client/ders'
import { enhancedApi as ticketingApi, type addTagTypes as ticketingTagTypes } from '~/data/api/client/ticketing'
import { enhancedApi as workflowsApi } from '~/data/api/client/workflows'
import type { CustomerWithPath } from '~/data/types/organization'
import type { PartialStore } from '~/store/types'

import { dispatcherFactory, selectorHookFactory } from '../utils'

type CloakedOrg = { name?: string; id: string }
type CurrentOrganizationStore = PartialStore<typeof currentOrganizationSlice>
type CurrentOrganizationState = {
  principalOrgId: string
  cloakedOrgs: CloakedOrg[]
}

const initialState: CurrentOrganizationState = {
  principalOrgId: '',
  cloakedOrgs: []
}

const currentOrganizationSlice = createSlice({
  name: 'currentOrganization',
  initialState,
  reducers: {
    setPrincipalOrganization: (state, action: PayloadAction<string | undefined>) => {
      const id = action.payload
      if (id === undefined) return

      state.principalOrgId = id
      state.cloakedOrgs = [{ id }]
    },
    cloakAsOrganization: (state, action: PayloadAction<CustomerWithPath>) => {
      state.cloakedOrgs = action.payload.path
    },
    revertToPreviousOrganization: (state) => {
      if (state.cloakedOrgs.length > 1) {
        state.cloakedOrgs.pop()
      }
    },
    resetCloaking: (state) => {
      // Practically speaking this should always be true
      state.cloakedOrgs = [{ id: state.principalOrgId }]
    }
  }
})

export const { setPrincipalOrganization, cloakAsOrganization, revertToPreviousOrganization, resetCloaking } =
  currentOrganizationSlice.actions

export default currentOrganizationSlice.reducer

/**
 * Tag types whose data is scoped by the `X-Organization-Id` header (injected in `prepareHeaders`
 * from this slice) rather than by query args. Because the header isn't part of the RTK Query cache key,
 * these caches must be evicted explicitly whenever the effective (cloaked) organization changes.
 *
 * Most billing data lives on the dedicated billing client, but the payment-terminal routes are still served
 * by the shared backend and therefore remain on the root client — so the two sets are invalidated on their
 * respective clients below.
 */
// Includes the v2 tax caches (the legacy v1 'Tax' tag left with the /api/v1/taxes endpoints).
type BillingTag = (typeof addTagTypes)[number]
const ORG_SCOPED_BILLING_TAGS = [
  'Agreements',
  'AssistantBillingV1',
  'Billing',
  'Billing Operations',
  'Billing Overview',
  'Connected Account Tax Sync',
  'ConnectedAccountV1',
  'Customer',
  'Invoice Artifact Access',
  'Invoices',
  'InvoicesV2',
  'Merchant Tax Settings',
  'Payments',
  'Payouts',
  'Products',
  'Reimbursements',
  'StripeTerminalV1',
  'Tax Applications',
  'Tax Profiles',
  'Tax Rates',
  'Tax Registrations'
] as const satisfies readonly BillingTag[]

const ORG_SCOPED_ROOT_TAGS = ['Payment Terminal Sessions', 'Payment Terminals'] as const

/**
 * Workflows data is scoped entirely by the `X-Organization-Id` header (the list/detail/run queries carry
 * no org id in their args), so — like billing — these caches must be evicted when the effective org changes.
 * Otherwise a workflow list fetched as the parent org keeps being served after cloaking into a child, since
 * the header isn't part of the RTK Query cache key.
 */
const ORG_SCOPED_WORKFLOWS_TAGS = ['workflows', 'workflow-versions', 'workflow-runs', 'in-app-notifications'] as const

/**
 * Single-entity DER reads (`getLoad`, `getGridConnection`, `getPanel`, `getFuelTank`, ...) take only the
 * entity id in their args, so the effective org lives purely in the `X-Organization-Id` header and never
 * reaches the RTK cache key. Detail pages read one of these directly whenever the org-scoped list cache
 * has not got the entity — a freshly created one, or one the entity's feature gate withholds — and without
 * eviction a cloak change would serve the previous org's entity under the new org's header.
 *
 * SynopLink reads are the same shape and the same risk: a parent org can read a descendant's SynopLink,
 * a descendant cannot read the parent's, and neither fact is in the cache key. Evicting the tag covers
 * every SynopLink endpoint, which is the intent — cloaking should not carry a device's state across.
 */
const ORG_SCOPED_DERS_TAGS = ['Distributed Energy Resources', 'Synop Link', 'Link Controls'] as const

/**
 * Every ticketing read is scoped by the organization header (the pages do not put the org id in the args),
 * and the cursor-paged lists are not keyed by it either, so the whole client is evicted on a cloak change.
 */
const ORG_SCOPED_TICKETING_TAGS = [
  'Tickets',
  'Projects',
  'Tasks',
  'Comments',
  'Task Activity Events',
  'Work Orders',
  'Task Templates',
  'Project Templates',
  'Assets',
  'Project assets',
  'Attachments',
  'Documents',
  'Plans',
  'Email Intake',
  'Agent Sessions'
] as const satisfies readonly (typeof ticketingTagTypes)[number][]

/**
 * Evicts header-scoped caches whenever the effective organization changes (principal, cloak, revert, reset).
 * `invalidateTags` refetches currently-subscribed queries (now sending the new org header) and drops
 * unsubscribed cache entries, so stale data from the previous organization is never served.
 */
export const cloakingCacheMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)

  if (
    setPrincipalOrganization.match(action) ||
    cloakAsOrganization.match(action) ||
    revertToPreviousOrganization.match(action) ||
    resetCloaking.match(action)
  ) {
    store.dispatch(billingApi.util.invalidateTags([...ORG_SCOPED_BILLING_TAGS]))
    store.dispatch(rootApi.util.invalidateTags([...ORG_SCOPED_ROOT_TAGS]))
    store.dispatch(workflowsApi.util.invalidateTags([...ORG_SCOPED_WORKFLOWS_TAGS]))
    store.dispatch(dersApi.util.invalidateTags([...ORG_SCOPED_DERS_TAGS]))
    store.dispatch(ticketingApi.util.invalidateTags([...ORG_SCOPED_TICKETING_TAGS]))
    // The assistant client's cache keys carry no organization, so nothing in it survives a change of organization.
    store.dispatch(assistantApi.util.resetApiState())
  }

  return result
}

const selectPrincipalOrgId = (state: CurrentOrganizationStore) => state.currentOrganization.principalOrgId
const selectCurrentOrgId = (state: CurrentOrganizationStore) => state.currentOrganization.cloakedOrgs.at(-1)?.id ?? ''
const selectCloakedOrgs = (state: CurrentOrganizationStore) => state.currentOrganization.cloakedOrgs

const selectIsCloaking = createSelector(selectCurrentOrgId, selectPrincipalOrgId, (currentOrgId, principalOrgId) => {
  return principalOrgId !== currentOrgId
})

const selectCloakingInfo = createSelector(selectCloakedOrgs, selectIsCloaking, (cloakedOrgs, isCloaking) => {
  return { cloakedOrgs, isCloaking }
})

export const useCurrentOrgId = selectorHookFactory(selectCurrentOrgId)
export const useCloakingInfo = selectorHookFactory(selectCloakingInfo)
export const usePrincipalOrgId = selectorHookFactory(selectPrincipalOrgId)
export const useIsCloaking = selectorHookFactory(selectIsCloaking)

export const useSetPrincipleOrganization = dispatcherFactory(setPrincipalOrganization)
export const useCloakAsOrganization = dispatcherFactory(cloakAsOrganization)
export const useRevertToPreviousOrganization = dispatcherFactory(revertToPreviousOrganization)

/**
 * Clears the cloak stack back to the principal org and resets org-scoped
 * overview UI state (same side effects as `useCloaking`).
 */
export function useResetCloaking() {
  const dispatch = useDispatch()

  return useCallback(() => {
    dispatch(resetCloaking())
    dispatch(resetVehiclesOverview())
    dispatch(resetSitesOverview())
  }, [dispatch])
}

/**
 * Initiates "cloaking" as the provided organization
 */
export function useCloaking() {
  const dispatch = useDispatch()
  const cloakAsOrganization = useCloakAsOrganization()

  return useCallback(
    (org: CustomerWithPath) => {
      // Update this slice to indicate the new cloaked org
      cloakAsOrganization(org)

      // Perform updates to other slices. Report filters are org-scoped in the
      // reports slice, so cloaking restores the target org's saved selections.
      dispatch(resetVehiclesOverview())
      dispatch(resetSitesOverview())
    },
    [dispatch, cloakAsOrganization]
  )
}
